import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Paper
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingIcon,
  CalendarToday as CalendarIcon,
  DateRange as DateRangeIcon,
  Event as EventIcon,
  AccessTime as TimeIcon
} from '@mui/icons-material';
import { WorkoutApiService } from '../../services/api/WorkoutApiService';
import { WorkoutCount } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface WorkoutStatsCompactProps {
  onServiceUnavailable?: () => void;
  hideHeader?: boolean;
  noCard?: boolean;
}

export const WorkoutStatsCompact: React.FC<WorkoutStatsCompactProps> = ({ onServiceUnavailable, hideHeader = false, noCard = false }) => {
  const [workoutCount, setWorkoutCount] = useState<WorkoutCount | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useLanguage();

  const fetchWorkoutCount = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await WorkoutApiService.getWorkoutCount();
      setWorkoutCount(data);
    } catch (err: any) {
      console.error('Error fetching workout count:', err);
      if (err.isServiceUnavailable && onServiceUnavailable) {
        onServiceUnavailable();
      } else {
        setError(t('workoutStats.error') || 'Failed to load workout statistics');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkoutCount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRefresh = () => {
    fetchWorkoutCount();
  };

  if (loading) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={60}>
            <CircularProgress size={24} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1 }}>
          <Alert 
            severity="error" 
            action={
              <IconButton size="small" onClick={handleRefresh}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            }
          >
            {error}
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!workoutCount) {
    return null;
  }

  const StatCard: React.FC<{
    label: string;
    value: number;
    icon: React.ReactNode;
    gradient: string;
  }> = ({ label, value, icon, gradient }) => (
    <Paper
      elevation={2}
      sx={{
        p: 2,
        background: gradient,
        borderRadius: 2,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4,
        },
      }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Typography
          variant="body2"
          sx={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontWeight: 500,
            fontSize: '0.813rem',
          }}
        >
          {label}
        </Typography>
        <Box sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
          {icon}
        </Box>
      </Box>
      <Typography
        variant="h4"
        sx={{
          color: 'white',
          fontWeight: 'bold',
          fontSize: { xs: '1.75rem', sm: '2rem' },
        }}
      >
        {value}
      </Typography>
    </Paper>
  );

  const statsContent = (
    <>
      {!hideHeader && (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" gap={1}>
            <TrendingIcon color="primary" />
            <Typography variant="h6" fontWeight="bold">
              {t('workoutStats.title') || 'Estadísticas de Entrenamiento'}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        <StatCard
          label={t('workoutStats.thisWeek') || 'Esta Semana'}
          value={workoutCount.thisWeek}
          icon={<CalendarIcon fontSize="small" />}
          gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
        />
        <StatCard
          label={t('workoutStats.last7Days') || 'Últimos 7 Días'}
          value={workoutCount.last7Days}
          icon={<TimeIcon fontSize="small" />}
          gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
        />
        <StatCard
          label={t('workoutStats.thisMonth') || 'Este Mes'}
          value={workoutCount.thisMonth}
          icon={<EventIcon fontSize="small" />}
          gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
        />
        <StatCard
          label={t('workoutStats.last30Days') || 'Últimos 30 Días'}
          value={workoutCount.last30Days}
          icon={<DateRangeIcon fontSize="small" />}
          gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
        />
      </Box>
    </>
  );

  if (noCard) {
    return <Box sx={{ p: 2 }}>{statsContent}</Box>;
  }

  return (
    <Card sx={{ mb: 2, overflow: 'visible' }}>
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        {statsContent}
      </CardContent>
    </Card>
  );
};